import React, { useState, useRef, useMemo } from 'react';
import './ResetPassword.css';
import { Box, Typography } from '@mui/material';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { Lock, CheckCircle, Check, Circle } from 'lucide-react';
import LogoImage from '../../../assets/Admin/AdminNavbar/logo-image.png';
import { useLocation } from 'react-router-dom';

const PASSWORD_RULES = {
  minLength: /.{8,40}/,
  uppercase: /[A-Z]/,
  lowercase: /[a-z]/,
  specialChar: /[ -/:-@[-`{-~]/,
};

const PASSWORD_RULE_LABELS = [
  { key: 'minLength', label: 'At least 8 characters' },
  { key: 'uppercase', label: 'At least one uppercase letter (A-Z)' },
  { key: 'lowercase', label: 'At least one lowercase letter (a-z)' },
  { key: 'specialChar', label: 'At least one special character' },
];

const ResetPassword = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [successMessage, setSuccessMesssage] = useState(false);
  const [passwordError, setPasswordError] = useState(null);
  const passwordInputRef = useRef(null);
  const confirmPasswordInputRef = useRef(null);

  const query = new URLSearchParams(useLocation().search);
  const token = query.get('token');

  const SERVER_API_URL = process.env.REACT_APP_SERVER_API_URL;

  const ruleChecks = useMemo(
    () => ({
      minLength: PASSWORD_RULES.minLength.test(password),
      uppercase: PASSWORD_RULES.uppercase.test(password),
      lowercase: PASSWORD_RULES.lowercase.test(password),
      specialChar: PASSWORD_RULES.specialChar.test(password),
    }),
    [password]
  );

  const allRulesPassed = Object.values(ruleChecks).every(Boolean);

  const handleUserPasswordChange = e => {
    setPassword(e.target.value);
    setPasswordError(null);
  };
  const handleUserConfirmPasswordChange = e => {
    setConfirmPassword(e.target.value);
    setPasswordError(null);
  };
  const handleClickShowPassword = () => {
    setShowPassword(prev => !prev);
  };
  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword(prev => !prev);
  };

  const password_validate = password => {
    if (!PASSWORD_RULES.minLength.test(password)) {
      return 'Password must be at least 8 characters long.';
    }
    if (!PASSWORD_RULES.uppercase.test(password)) {
      return 'Password must include at least one uppercase letter.';
    }
    if (!PASSWORD_RULES.lowercase.test(password)) {
      return 'Password must include at least one lowercase letter.';
    }
    if (!PASSWORD_RULES.specialChar.test(password)) {
      return 'Password must include at least one special character.';
    }

    return null;
  };

  const handleSubmit = async e => {
    e.preventDefault();

    const passwordValidationError = password_validate(password);
    if (passwordValidationError) {
      setPasswordError(passwordValidationError);
      return;
    }

    if (password !== confirmPassword) {
      toast.dismiss();
      toast.error('Passwords do not match.');
      return;
    }

    try {
      const newData = {
        token: token,
        password: password,
      };

      const res = await axios.post(`${SERVER_API_URL}/auth/reset-password`, newData);
      if (res?.status === 200) {
        setSuccessMesssage(true);
        setPasswordError(null);
      }
    } catch (error) {
      console.error('Reset password failed:', error);
      toast.dismiss();
      const apiDetail = error?.response?.data?.detail;
      const fallback = error?.message || 'Something went wrong. Please try again.';
      toast.error(apiDetail || fallback);
    }
  };

  const isFormValid =
    password.trim() !== '' &&
    confirmPassword.trim() !== '' &&
    allRulesPassed &&
    password === confirmPassword;

  return (
    <Box className="reset-password-container">
      {/* Header Section */}
      <Box className="reset-password-header">
        <Box className="reset-password-logo">
          <img src={LogoImage} alt="Klass Online Logo" className="logo-image" />
        </Box>
      </Box>

      {/* Main Content - Card */}
      {successMessage === false ? (
        <Box className="reset-password-card">
          <Box className="card-header">
            <Lock className="card-icon email-password-icon" />
            <Typography component="h3" className="card-title">
              Reset Password
            </Typography>
          </Box>

          <Typography variant="body2" className="card-description">
            Enter your new password below
          </Typography>

          <form className="card-form" onSubmit={e => handleSubmit(e)}>
            <Box className="input-group">
              <label htmlFor="password" className="input-label">
                New Password
              </label>
              <Box className="input-container">
                <Lock className="input-icon" />
                <input
                  id="password"
                  className="input-field"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter new password"
                  value={password}
                  onChange={handleUserPasswordChange}
                  ref={passwordInputRef}
                  required
                  autoComplete="new-password"
                />
                <div
                  onClick={handleClickShowPassword}
                  className="password-toggle"
                  aria-label="toggle password visibility"
                >
                  {showPassword ? (
                    <VisibilityOff className="toggle-icon" />
                  ) : (
                    <Visibility className="toggle-icon" />
                  )}
                </div>
              </Box>
              {passwordError && <div className="error-message">{passwordError}</div>}
              <Box className="password-rules-container">
                <Typography className="password-rules-title">Password must contain:</Typography>
                <ul className="password-rules-list">
                  {PASSWORD_RULE_LABELS.map(rule => {
                    const passed = ruleChecks[rule.key];
                    return (
                      <li
                        key={rule.key}
                        className={`password-rule ${
                          passed ? 'password-rule-passed' : 'password-rule-pending'
                        }`}
                      >
                        {passed ? (
                          <Check className="password-rule-icon password-rule-icon-passed" />
                        ) : (
                          <Circle className="password-rule-icon password-rule-icon-pending" />
                        )}
                        <span>{rule.label}</span>
                      </li>
                    );
                  })}
                </ul>
              </Box>
            </Box>

            <Box className="input-group">
              <label htmlFor="confirmPassword" className="input-label">
                Confirm Password
              </label>
              <Box className="input-container">
                <Lock className="input-icon" />
                <input
                  id="confirmPassword"
                  className="input-field"
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Re-enter new password"
                  value={confirmPassword}
                  onChange={handleUserConfirmPasswordChange}
                  ref={confirmPasswordInputRef}
                  required
                  autoComplete="new-password"
                />
                <div
                  onClick={handleClickShowConfirmPassword}
                  className="password-toggle"
                  aria-label="toggle password visibility"
                >
                  {showConfirmPassword ? (
                    <VisibilityOff className="toggle-icon" />
                  ) : (
                    <Visibility className="toggle-icon" />
                  )}
                </div>
              </Box>
              {password !== confirmPassword && confirmPassword !== '' && (
                <div className="error-message">Passwords do not match.</div>
              )}
            </Box>

            <button
              type="submit"
              className={`primary-button ${!isFormValid ? 'disabled' : ''}`}
              disabled={!isFormValid}
              data-testid="submit"
            >
              Confirm Password
            </button>
          </form>
        </Box>
      ) : (
        <Box className="reset-password-card">
          <Box className="card-header">
            <Box className="success-icon-container">
              <CheckCircle className="card-icon success-icon" />
            </Box>
            <Typography component="h3" className="card-title">
              Password changed successfully
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ResetPassword;
